import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "../styles/ReviewManager.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const ReviewManager = ({
  taskName,
  taskId,
  referenceId = null,
  userId,
  isAdmin,
}) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState("not_completed");
  const [assignedTasks, setAssignedTasks] = useState([]);

  /**
   * ✅ Reusable function to fetch the latest review status
   */
  const fetchStatus = async () => {
    try {
      const res = await axios.get(
        `${API_BASE}/api/usertasks/status/${userId}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      const key = referenceId ? `${taskName}_${referenceId}` : taskId;
      const taskStatus =
        res.data?.status?.[key] ??
        res.data?.status?.[taskName] ??
        "not_completed";

      setStatus(taskStatus);
    } catch (err) {
      console.error("❌ Failed to fetch task status", err);
    }
  };

  /**
   * Load current task status when props change
   */
  useEffect(() => {
    if (userId && taskId) fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, taskId, taskName, referenceId]);

  /**
   * Load user tasks dynamically (for dropdown navigation)
   */
  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/dropdowns/user-tasks/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setAssignedTasks(res.data || []);
      } catch (e) {
        console.error("❌ Failed to load user tasks", e);
      }
    })();
  }, [userId]);

  const orderedTasks = useMemo(() => assignedTasks || [], [assignedTasks]);

  const currentIndex = useMemo(() => {
    const idx = orderedTasks.findIndex(
      (t) => Number(t.ut_task_id) === Number(taskId)
    );
    return idx >= 0 ? idx : 0;
  }, [orderedTasks, taskId]);

  /**
   * Navigation between tasks
   */
  const goToTask = (task) => {
    if (!task) return;
    const viewingUserId = userId;
    const currentUserId = Number(localStorage.getItem("userId"));
    const adminRole = localStorage.getItem("userRole") === "1";

    if (task.t_key === "consent_history") {
      navigate(`/consent/list/${viewingUserId}`);
      return;
    }

    if (task.t_key === "user_information") {
      if (adminRole && currentUserId !== Number(viewingUserId)) {
        navigate(`/edit-user/${viewingUserId}`);
      } else {
        navigate(`/profile`);
      }
      return;
    }

    if (!task.t_route) return;
    const path = task.t_route
      .replace(":userId", String(viewingUserId))
      .replace(":id", String(viewingUserId));
    navigate(path);
  };

  const goNext = () => {
    if (orderedTasks.length === 0) return;
    const nextIndex = (currentIndex + 1) % orderedTasks.length;
    goToTask(orderedTasks[nextIndex]);
  };

  const handleJump = (e) => {
    const tid = Number(e.target.value);
    const t = orderedTasks.find((x) => Number(x.ut_task_id) === tid);
    goToTask(t);
  };

  /**
   * ✅ Mark as reviewed — updated to refresh status immediately
   */
  const markReviewed = async () => {
    if (!isAdmin) return;

    try {
      let endpoint;

      switch (taskName) {
        case "vacation_form":
          endpoint = `${API_BASE}/api/vacationform/review/complete/${referenceId}`;
          break;
        case "cvsa_inspections":
          endpoint = `${API_BASE}/api/cvsa/review/complete/${referenceId}`;
          break;
        case "interview":
          endpoint = `${API_BASE}/api/interviews/review/complete/${referenceId}`;
          break;
        default:
          endpoint = `${API_BASE}/api/usertasks/review`;
          break;
      }

      const confirmReview = window.confirm(
        "Are you sure you want to mark this task as Reviewed?"
      );
      if (!confirmReview) return;

      if (endpoint.includes("/complete/")) {
        await axios.put(endpoint, null, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
      } else {
        await axios.post(
          endpoint,
          { userId, taskId, referenceId },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      }

      // ✅ Immediately refresh from backend
      await fetchStatus();

      alert("✔️ Task marked as reviewed!");
    } catch (err) {
      console.error("❌ Failed to mark as reviewed", err);
      alert("❌ Failed to mark as reviewed. Please try again.");
    }
  };

  /**
   * Move back to review state
   */
  const unreview = async () => {
    if (!isAdmin) return;
    const confirmUnreview = window.confirm(
      "Do you really want to move this task back to review?"
    );
    if (!confirmUnreview) return;

    try {
      let endpoint;
      switch (taskName) {
        case "vacation_form":
          endpoint = `${API_BASE}/api/vacationform/review/reset/${referenceId}`;
          break;
        case "cvsa_inspections":
          endpoint = `${API_BASE}/api/cvsa/review/reset/${referenceId}`;
          break;
        case "interview":
          endpoint = `${API_BASE}/api/interviews/review/reset/${referenceId}`;
          break;
        default:
          endpoint = `${API_BASE}/api/usertasks/unreview`;
          break;
      }

      if (endpoint.includes("/reset/")) {
        await axios.put(endpoint, null, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
      } else {
        await axios.post(
          endpoint,
          { userId, taskId, referenceId },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      }

      // ✅ Refresh after unreview
      await fetchStatus();

      alert("✔️ Task moved back to review.");
    } catch (err) {
      console.error("❌ Failed to move back to review", err);
      alert("❌ Failed to move back to review. Please try again.");
    }
  };

  /**
   * Render current status label
   */
  const renderStatusLabel = () => {
    if (status === "completed") {
      return (
        <div className="status-wrapper">
          {isAdmin && (
            <div className="status-badge edit" onClick={unreview}>
              Unreview
            </div>
          )}
          <div className="status-badge completed">Reviewed ✔️</div>
        </div>
      );
    }
    if (isAdmin) {
      return (
        <div className="status-wrapper">
          <div className="status-badge review clickable" onClick={markReviewed}>
            Mark as Reviewed
          </div>
        </div>
      );
    }
    return null;
  };

  /**
   * ✅ Component render
   */
  return (
    <div className="review-manager-bar mt-5">
      {/* Left section: dropdown + next button */}
      <div className="left-section">
        <select
          className="form-select form-select-sm d-inline w-auto"
          value={Number(taskId)}
          onChange={handleJump}
          disabled={orderedTasks.length === 0}
        >
          {orderedTasks.map((t) => (
            <option key={t.ut_task_id} value={t.ut_task_id}>
              {t.t_title || `Task ${t.ut_task_id}`}
            </option>
          ))}
        </select>

        <button
          className="btn btn-sm btn-light ms-2"
          onClick={goNext}
          disabled={orderedTasks.length === 0}
        >
          Next ➡️
        </button>
      </div>

      {/* Right section: status display */}
      <div className="right-section">{renderStatusLabel()}</div>
    </div>
  );
};

export default ReviewManager;
